// @src/pre-deployment/src/components/TrackingService/services/ContentDiscoveryService.js v2.0
// - Migrated from src/pre-deployment/src/tracking/contentDiscovery.js
// - Part of TrackingService component migration
// - Enhanced with ErrorHandling integration via silentTrack options

import { getDocs, collection } from 'firebase/firestore';

import { db } from '../../../services/firebase.js';

import { silentTrack } from './SilentTrackService.js';

/**
 * ContentDiscoveryService - Discovers vocabulary and grammar content from Firestore
 * Used to scan content collections and find available learning items for a chapter.
 */
export const ContentDiscoveryService = {
    /**
     * Discover vocabulary content for a chapter
     * Scans: feature_testing/Korea_University/Level{level}/Vocab/Chapter{chapter}
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {string} [institution='Korea_University'] - Institution identifier
     * @returns {Promise<Array<{id: string, data: Object}>|null>} Array of vocabulary items or null on error
     */
    async discoverVocabularyContent(level, chapter, institution = 'Korea_University') {
        const collectionPath = `feature_testing/${institution}/Level${level}/Vocab/Chapter${chapter}`;

        return silentTrack(async () => {
            const querySnapshot = await getDocs(collection(db, collectionPath));

            const vocabularyContent = [];
            querySnapshot.forEach((doc) => {
                vocabularyContent.push({
                    id: doc.id,
                    data: doc.data()
                });
            });

            console.log(`Discovered ${vocabularyContent.length} vocabulary items for Level ${level} Chapter ${chapter}`);
            return vocabularyContent;
        }, 'discover_vocabulary_content', {
            componentName: 'ContentDiscoveryService',
            severity: 'medium',
            level: level,
            chapter: chapter,
            firebasePath: collectionPath
        });
    },

    /**
     * Discover grammar content for a chapter
     * Scans: feature_testing/Korea_University/Level{level}/Grammar/Chapter{chapter}
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {string} [institution='Korea_University'] - Institution identifier
     * @returns {Promise<Array<{id: string, data: Object}>|null>} Array of grammar items or null on error
     */
    async discoverGrammarContent(level, chapter, institution = 'Korea_University') {
        const collectionPath = `feature_testing/${institution}/Level${level}/Grammar/Chapter${chapter}`;

        return silentTrack(async () => {
            const querySnapshot = await getDocs(collection(db, collectionPath));

            const grammarContent = [];
            querySnapshot.forEach((doc) => {
                grammarContent.push({
                    id: doc.id,
                    data: doc.data()
                });
            });

            console.log(`Discovered ${grammarContent.length} grammar items for Level ${level} Chapter ${chapter}`);
            return grammarContent;
        }, 'discover_grammar_content', {
            componentName: 'ContentDiscoveryService',
            severity: 'medium',
            level: level,
            chapter: chapter,
            firebasePath: collectionPath
        });
    },

    /**
     * Get content metadata for a chapter (vocabulary + grammar counts)
     * @param {number|string} level - Learning level
     * @param {number|string} chapter - Chapter number
     * @param {string} [institution='Korea_University'] - Institution identifier
     * @returns {Promise<{vocabulary: Array, grammar: Array, total: number}|null>} Content metadata or null on error
     */
    async getContentMetadata(level, chapter, institution = 'Korea_University') {
        return silentTrack(async () => {
            const vocabulary = await this.discoverVocabularyContent(level, chapter, institution);
            const grammar = await this.discoverGrammarContent(level, chapter, institution);

            return {
                vocabulary: vocabulary || [],
                grammar: grammar || [],
                total: (vocabulary?.length || 0) + (grammar?.length || 0)
            };
        }, 'get_content_metadata', {
            componentName: 'ContentDiscoveryService',
            severity: 'medium',
            level: level,
            chapter: chapter,
            firebasePath: `feature_testing/${institution}/Level${level}`
        });
    }
};

export default ContentDiscoveryService;
